import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Button, ButtonLabel, Header } from '@components/Generic'
import {
  GocardlessLoadErrorScreen,
  GocardlessLoadingScreen,
  GocardlessRedirectingScreen,
} from '@components/Gocardless/GocardlessScreens'
import { StoreContext } from '@store/Root.store'
import { devLog } from '@methods/devLog'
import { getUrlParam, setUrlParam } from '@methods/urlParams'
import { formatIban } from '@methods/formatIban'
import { goToIncomeConfirmation, redirectBackFromGocardless } from '@methods/gocardless'
import {
  IBAN_COUNTRY_URL_PARAM,
  IBAN_REFUSED_URL_PARAM,
  abortIbanSupplement,
  confirmIban,
  errorCode,
  forgetPreviewedIban,
  getIbanSupplementContext,
  leaveWhenNothingToAsk,
  previewIban,
  previewedIbanFor,
  wasRefused,
} from '@methods/gocardlessIbanSupplement'
import { cx } from '@/utils/cx'
import { IBAN_COUNTRY_ERRORS, type IbanSupplementErrorCode } from '@/types/gocardlessIbanSupplement'

// `/gocardless-iban-confirmation`, step 1: the IBAN the chosen country gives the account number, for
// the user to confirm. The country travels in the URL. Coming from the country step, the IBAN it just
// previewed is shown right away; otherwise (a reload, a direct link) the session is checked and the
// IBAN asked from the backend again (`preview`), so the screen is the same.
// "Yes" stores the IBAN (`confirm`) and the backend completes the session, which may pause once more
// on the income confirmation screen; otherwise the session is handed back through `redirect-back`.
// "No" goes back to the country step. A refused answer that the session cannot recover from (no
// account number, a request the backend cannot read) is a dead end: the session is failed (`abort`)
// and handed back, as Cancel does on the country step.

type Status = 'loading' | 'loadError' | 'confirm' | 'redirecting'
// `final`: the backend refused the answer (a 400 or 404, see `wasRefused`) while the session still
// waits for it - a retry cannot succeed, the only way out is `abortAndRedirectBack`
type SubmitError = 'retryable' | 'final' | null

export const GocardlessIbanConfirmationPage = observer(() => {
  const store = useContext(StoreContext)
  const { gocardlessIbanSupplementPage: trans } = store.TranslationsState.translations

  const countryCode = getUrlParam(IBAN_COUNTRY_URL_PARAM)
  // the IBAN the country step previewed a moment ago, when this is the step after it
  const previewedIban = countryCode ? previewedIbanFor(countryCode) : undefined

  const [status, setStatus] = useState<Status>(previewedIban ? 'confirm' : 'loading')
  const [iban, setIban] = useState<string>(previewedIban ?? '')
  const [pending, setPending] = useState(false)
  const [submitError, setSubmitError] = useState<SubmitError>(null)
  // the way out of the dead end failed (the network, a 5xx): the button is worth another try
  const [abortFailed, setAbortFailed] = useState(false)

  const redirectBack = () => {
    setStatus('redirecting')
    redirectBackFromGocardless()
  }

  // Back to the country step as a page of its own, not history.back(): the confirmation URL may have
  // been opened directly (a reload in a new tab, a pasted link) with nothing to go back to. The
  // user's "No" pushes a history entry like any step; when this page sends the user back on its own
  // it replaces its entry instead, or Back would land here only to be sent back again. `refused`
  // is the backend's reason, shown on the country step (the country does not fit the account number).
  const backToCountry = (options: { refused?: IbanSupplementErrorCode; byPage?: boolean } = {}) => {
    if (options.refused) {
      setUrlParam(IBAN_REFUSED_URL_PARAM, options.refused)
    }

    if (options.byPage) {
      store.ScenarioState.replacePage(0)
    } else {
      store.ScenarioState.setCurrentPage(0)
    }
  }

  const load = async () => {
    setStatus('loading')

    try {
      const context = await getIbanSupplementContext()

      if (leaveWhenNothingToAsk(context)) {
        setStatus('redirecting')
        return
      }

      // opened without the country step (a direct link): nothing to confirm yet
      if (!countryCode) {
        backToCountry({ byPage: true })
        return
      }

      const preview = await previewIban(countryCode)
      setIban(preview.iban)
      setStatus('confirm')
    } catch (e) {
      devLog('Error loading the IBAN confirmation screen:', e)

      // the country no longer fits (the URL was edited): choose again, told why; anything else the
      // country step sorts out as well (it checks the session and knows the dead ends)
      if (wasRefused(e)) {
        const reason = errorCode(e)
        backToCountry({
          byPage: true,
          refused: reason && IBAN_COUNTRY_ERRORS.has(reason) ? reason : undefined,
        })
        return
      }

      setStatus('loadError')
    }
  }

  useEffect(() => {
    if (previewedIban) {
      // read once: a confirmation screen mounted again (Back, Forward) asks the backend
      forgetPreviewedIban()
    } else {
      void load()
    }
  }, [])

  // The way out of a dead end: the session fails for want of an IBAN and is handed back. A session
  // that already ended is handed back all the same; otherwise (the network, a 5xx) the button stays.
  const abortAndRedirectBack = async () => {
    if (pending) return

    setPending(true)
    setAbortFailed(false)
    try {
      await abortIbanSupplement()
    } catch (e) {
      devLog('Error aborting the IBAN supplement:', e)

      if (!wasRefused(e)) {
        setAbortFailed(true)
        setPending(false)
        return
      }
    }

    redirectBack()
  }

  const handleConfirm = async () => {
    if (pending || !countryCode) return

    setPending(true)
    setSubmitError(null)

    try {
      const result = await confirmIban(countryCode, iban)

      setStatus('redirecting')
      if (result.incomeConfirmationRequired) {
        goToIncomeConfirmation()
      } else {
        redirectBackFromGocardless()
      }
    } catch (e) {
      devLog('Error confirming the IBAN:', e)

      if (wasRefused(e)) {
        // A session no longer waiting for the answer, or an answer the backend refused. The first
        // also happens when a retry follows a lost response - then the IBAN is stored and the
        // context says where the user goes. A failed lookup decides nothing: the user may retry.
        let context
        try {
          context = await getIbanSupplementContext()
        } catch (lookupError) {
          devLog('Error reading the IBAN context:', lookupError)
          setSubmitError('retryable')
          setPending(false)
          return
        }

        if (leaveWhenNothingToAsk(context)) {
          setStatus('redirecting')
          return
        }

        // still waiting, but the IBAN shown is not the one the backend builds (a stale or edited
        // URL): choose the country again, told why
        const reason = errorCode(e)
        if (reason === 'ibanMismatch' || (reason && IBAN_COUNTRY_ERRORS.has(reason))) {
          backToCountry({ byPage: true, refused: reason })
          return
        }

        setSubmitError('final')
      } else {
        setSubmitError('retryable')
      }

      setPending(false)
    }
  }

  if (status === 'loading') {
    return <GocardlessLoadingScreen className="w-500 p-40" />
  }

  if (status === 'redirecting') {
    return (
      <GocardlessRedirectingScreen
        className="w-500 p-40"
        header={trans?.redirectingHeader}
        label={trans?.redirectingLabel}
        buttonLabel={trans?.pleaseWait}
      />
    )
  }

  if (status === 'loadError') {
    return (
      <GocardlessLoadErrorScreen
        className="w-500 p-40"
        header={trans?.confirmHeader}
        message={trans?.loadError}
        retryLabel={trans?.tryAgain}
        retryTestId="iban-confirm-retry"
        onRetry={() => void load()}
      />
    )
  }

  const locked = pending || submitError === 'final'

  return (
    <div className="page-container mobile-m-0 w-500 p-40">
      <Header className="no-margin">{trans?.confirmHeader}</Header>

      <p className="generic__paragraph">{trans?.confirmDescription}</p>

      <div className="generic__label">{trans?.ibanLabel}</div>
      <div className="elavon__expected-name-wrapper" data-testid="iban-confirm-value">
        {formatIban(iban)}
      </div>

      {submitError && (
        <p className="generic__paragraph generic__error-message" role="alert">
          {submitError === 'final' ? trans?.deadEnd : trans?.submitError}
        </p>
      )}

      {abortFailed && (
        <p className="generic__paragraph generic__error-message" role="alert">
          {trans?.submitError}
        </p>
      )}

      <div
        className={cx(
          'layout__content-container-bottom-fixed',
          'layout__content-container-bottom-fixed--no-border',
          'layout__content-container-bottom-fixed--stacked'
        )}
      >
        {submitError === 'final' ? (
          <Button
            className="button--w164"
            onClick={() => void abortAndRedirectBack()}
            disabled={pending}
            dataTestid="iban-confirm-back"
          >
            <ButtonLabel isLoading={pending} label={trans?.backToOnboarding} />
          </Button>
        ) : (
          <>
            <Button
              className="button--link"
              onClick={() => backToCountry()}
              disabled={locked}
              dataTestid="iban-confirm-incorrect"
            >
              {trans?.incorrect}
            </Button>

            <Button
              onClick={() => void handleConfirm()}
              disabled={locked}
              dataTestid="iban-confirm-correct"
            >
              <ButtonLabel isLoading={pending} label={trans?.correct} />
            </Button>
          </>
        )}
      </div>
      <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
    </div>
  )
})
